import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import calendarService from "../../services/calendarService";
import { getErrorMessage } from "../../utils/errorHandler";
import FormCard from "../../components/ui/FormCard";
import FormActions from "../../components/ui/FormActions";
import Breadcrumb from "../../components/ui/Breadcrumb";
import { Edit, Trash2, Calendar, Clock, MapPin } from "lucide-react";
import "../../styles/FormCard.css";

const CalendarEventDetail = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    loadEvent();
  }, [id]);

  const loadEvent = async () => {
    try {
      setLoading(true);
      const response = await calendarService.getEventById(id);
      setEvent(response?.data || response);
    } catch (err) {
      console.error("Error loading event:", err);
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/rh/calendar/events/${id}/edit`);
  };

  const handleDelete = async () => {
    try {
      await calendarService.deleteEvent(id);
      toast.success("Jour férié supprimé avec succès");
      navigate("/rh/calendar");
    } catch (err) {
      console.error("Error deleting event:", err);
      toast.error(getErrorMessage(err));
    }
  };

  const handleBack = () => {
    navigate("/rh/calendar");
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="error-state">
        <p>Jour férié non trouvé</p>
      </div>
    );
  }

  const eventTypeLabels = {
    PAID_HOLIDAY: "Chômé payé",
    UNPAID_HOLIDAY: "Chômé non payé",
    MEETING: "Réunion RH",
    TRAINING: "Formation",
    INTERVIEW: "Entretien",
    INTERNAL_EVENT: "évènement interne",
    HOLIDAY: "Jour férié",
    RELIGIOUS_EVENT: "Fèªte religieuse / Hijri",
    OTHER: "Autre",
  };

  const breadcrumbItems = [
    { label: "RH", to: "/rh/dashboard" },
    { label: "Calendrier", to: "/rh/calendar" },
    { label: event.title }
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Breadcrumb items={breadcrumbItems} />
      <FormCard title={event.title} showBackButton onBack={handleBack}>
        <div className="event-detail">
          <div className="event-detail-row">
            <div className="event-detail-item">
              <label>Type</label>
              <div className="event-detail-value">
                {eventTypeLabels[event.event_type] || event.event_type}
              </div>
            </div>
            <div className="event-detail-item">
              <label>Statut</label>
              <div className="event-detail-value">
                {event.status || "Planifié"}
              </div>
            </div>
          </div>

          <div className="event-detail-row">
            <div className="event-detail-item">
              <label>
                <Calendar size={16} />
                Date de début
              </label>
              <div className="event-detail-value">
                {event.start_date ? new Date(event.start_date).toLocaleDateString('fr-FR') : "N/A"}
              </div>
            </div>
            <div className="event-detail-item">
              <label>
                <Clock size={16} />
                Heure de début
              </label>
              <div className="event-detail-value">
                {event.start_time || "N/A"}
              </div>
            </div>
          </div>

          <div className="event-detail-row">
            <div className="event-detail-item">
              <label>
                <Calendar size={16} />
                Date de fin
              </label>
              <div className="event-detail-value">
                {event.end_date ? new Date(event.end_date).toLocaleDateString('fr-FR') : "N/A"}
              </div>
            </div>
            <div className="event-detail-item">
              <label>
                <Clock size={16} />
                Heure de fin
              </label>
              <div className="event-detail-value">
                {event.end_time || "N/A"}
              </div>
            </div>
          </div>

          {event.location && (
            <div className="event-detail-row">
              <div className="event-detail-item full-width">
                <label>
                  <MapPin size={16} />
                  Localisation
                </label>
                <div className="event-detail-value">{event.location}</div>
              </div>
            </div>
          )}

          {event.description && (
            <div className="event-detail-row">
              <div className="event-detail-item full-width">
                <label>Description</label>
                <div className="event-detail-value">{event.description}</div>
              </div>
            </div>
          )}

          {event.created_by && (
            <div className="event-detail-row">
              <div className="event-detail-item">
                <label>Créé par</label>
                <div className="event-detail-value">{event.created_by}</div>
              </div>
              <div className="event-detail-item">
                <label>Origine</label>
                <div className="event-detail-value">{event.origin || "Manuel"}</div>
              </div>
            </div>
          )}
        </div>

        <FormActions
          onCancel={handleBack}
          onSave={handleEdit}
          saveText="Modifier"
          extraActions={
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="delete-button"
            >
              <Trash2 size={18} />
              Supprimer
            </button>
          }
        />

        {showDeleteConfirm && (
          <div className="confirm-dialog">
            <div className="confirm-dialog-content">
              <h3>Confirmer la suppression</h3>
              <p>èŠtes-vous sè»r de vouloir supprimer cet événement ?</p>
              <div className="confirm-dialog-actions">
                <button onClick={() => setShowDeleteConfirm(false)} className="cancel-button">
                  Annuler
                </button>
                <button onClick={handleDelete} className="delete-button">
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        )}
      </FormCard>
    </div>
  );
};

export default CalendarEventDetail;

